import Axios from 'axios';
import type { InternalAxiosRequestConfig } from 'axios';
import { environment } from '@/environments/environment';
import {
  getAccessToken,
  notifyUnauthorized,
  refreshAccessToken,
} from './auth-session';

const LOGIN_PATH = '/api/auth/login';
const REFRESH_PATH = '/api/auth/refresh';

interface AuthAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

function isAuthBypassRequest(url: string | undefined): boolean {
  if (!url) {
    return false;
  }

  return url.includes(LOGIN_PATH) || url.includes(REFRESH_PATH);
}

function authRequestInterceptor(config: InternalAxiosRequestConfig) {
  const token = getAccessToken();
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  return config;
}

export const api = Axios.create({
  baseURL: `${environment.apiUrl}`,
});

api.interceptors.request.use(authRequestInterceptor);
api.interceptors.response.use(
  response => {
    return response.data;
  },
  async error => {
    const config = error.config as AuthAxiosRequestConfig | undefined;
    const status = error.response?.status;

    if (
      status === 401 &&
      config &&
      !config._retry &&
      !isAuthBypassRequest(config.url)
    ) {
      const refreshed = await refreshAccessToken();
      if (refreshed) {
        config._retry = true;
        const token = getAccessToken();
        if (token) {
          config.headers.set('Authorization', `Bearer ${token}`);
        }
        return api(config);
      }

      notifyUnauthorized();
    }

    return Promise.reject(error);
  },
);
